import { Form, Formik, type FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import CustomCheckboxInput from '../../../components/Form/custom-checkbox-input';
import CustomTextInput from '../../../components/Form/custom-text-input';
import { extractErrorsFromApi } from '../../../common/helpers';
import MyProfileReadonlyInfo from './MyProfileReadonlyInfo';
import RenderErrorBag from '../../../components/render-error-bag';
import { toast } from 'react-toastify';
import { type User } from '../../../common/types';
import { UserEditSchema } from '../user.schema';
import { UserRoleLabel } from '../../../common/constants';
import { useTranslation } from 'react-i18next';
import { useUpdateUserMutation } from '../user.api-slice';

interface IProps {
  isOpen: boolean;
  toggleEditModal: () => any;
  item: User | null;
}

const EditUser: React.FC<IProps> = ({ item, isOpen, toggleEditModal }) => {
  const [updateItem, { isLoading, isError, error, isSuccess, reset }] = useUpdateUserMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const { t } = useTranslation();
  const handleUpdateItem = async (values: User, actions: FormikHelpers<User>): Promise<any> => {
    if (item == null) {
      return;
    }
    await updateItem({ body: { ...values }, id: item.id });
    actions.resetForm();
    toggleEditModal();
  };

  useEffect(() => {
    if (isSuccess) {
      toast(t('users.edit.successMessage'));
      reset();
    }
    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [isError, isSuccess]);

  return (
    <>
      {isOpen && item != null ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-3xl font-semibold">{t('users.edit.title')}</h3>
                  <button
                    className="p-1 ml-auto border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={toggleEditModal}
                  >
                    <AiOutlineClose size={20} />
                  </button>
                </div>
                <div className="relative p-6 flex-auto w-full">
                  {errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
                  {
                    <Formik initialValues={item} onSubmit={handleUpdateItem} validationSchema={UserEditSchema}>
                      {({ isSubmitting }) => (
                        <Form className="w-full flex flex-col items-start px-2 space-y-2" autoComplete="off">
                          <MyProfileReadonlyInfo label="Email" type="text" value={item.email} />
                          <MyProfileReadonlyInfo
                            label="Role"
                            type="text"
                            value={item?.role != null ? UserRoleLabel[item.role] : ''}
                          />
                          <MyProfileReadonlyInfo label="Root User" type="boolean" value={item.isRootUser} />
                          <MyProfileReadonlyInfo label="Approved" type="boolean" value={item.isApproved} />
                          <CustomTextInput
                            type="text"
                            label="Fullname"
                            name="fullname"
                            divClass="w-full flex flex-col "
                            labelClass="w-full text-left text-gray-600"
                            inputClass="w-full my-1 px-4 py-1 rounded-md"
                          />
                          <CustomCheckboxInput
                            label="Active"
                            name="isActive"
                            value={item.isActive}
                            divClass="w-1/2 flex items-center"
                            labelClass="text-left mt-2 text-gray-600"
                            inputClass="p-2 mt-2 ml-4 rounded-md"
                          />
                          <div className="w-full flex space-x-2 py-6 mt-4 border-t border-solid border-slate-200 rounded-b">
                            <button
                              className="w-1/2 text-black px-5 py-2 rounded-lg bg-gray-200"
                              onClick={toggleEditModal}
                            >
                              {t('users.edit.cancelBtnLabel')}
                            </button>
                            <button
                              type="submit"
                              disabled={isLoading || isSubmitting}
                              className="w-1/2 text-white px-4 py-2 rounded-lg bg-[#1D8A43]"
                            >
                              {t('users.edit.saveBtnLabel')}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </>
  );
};

export default EditUser;
